import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const getAuthToken = () => {
    const adminData = sessionStorage.getItem('adminkhadamat');
    if (adminData) {
        try {
            const parsed = JSON.parse(adminData);
            // البحث عن التوكن في أماكن مختلفة
            if (parsed.data?.token?.access_token) {
              const token = String(parsed.data.token.access_token);
              return token;
            }
            if (parsed.data?.access_token) return parsed.data.access_token;
            if (parsed.data?.token) return parsed.data.token;
            if (parsed.token) return parsed.token;
            if (parsed.result?.token) return parsed.result.token;
            if (parsed.user?.token) return parsed.user.token;
            return null;
        } catch {
            return null;
        }
    }
    return null;
};

// Fetch all categories
export const fetchCategories = createAsyncThunk(
    'adminCategories/fetchCategories',
    async (_, { rejectWithValue }) => {
        try {
            const token = getAuthToken();
            const response = await axios.get(`${BASE_URL}/admin/categories`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });

            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'فشل في جلب الفئات');
        }
    }
);

// Update category
export const updateCategory = createAsyncThunk(
    'adminCategories/updateCategory',
    async ({ categoryId, formData }, { rejectWithValue }) => {
        try {
            const token = getAuthToken();
            const response = await axios.post(
                `${BASE_URL}/admin/categories/${categoryId}`,
                formData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept-Language': 'ar',
                        'lang': 'ar',
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            return response.data;
        } catch (error) {
            const errorData = error.response?.data;
            const validationErrors = errorData?.errors;

            if (validationErrors) {
                const messages = Object.values(validationErrors).flat();
                const combinedMessage = messages.join(' \n ');
                return rejectWithValue(combinedMessage || 'فشل في تحديث الفئة');
            }

            return rejectWithValue(errorData?.message || error.message || 'فشل في تحديث الفئة');
        }
    }
);

// Fetch subcategories
export const fetchSubCategories = createAsyncThunk(
    'adminCategories/fetchSubCategories',
    async ({ categoryId, search = '' }, { rejectWithValue }) => {
        try {
            const token = getAuthToken();
            const response = await axios.get(
                `${BASE_URL}/admin/category/${categoryId}/sub-categories`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept-Language': 'ar',
                        'lang': 'ar',
                    },
                    params: search ? { search } : {}
                }
            );

            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'فشل في جلب الفئات الفرعية');
        }
    }
);

// Add subcategory
export const addSubCategory = createAsyncThunk(
    'adminCategories/addSubCategory',
    async (data, { rejectWithValue }) => {
        try {
            const token = getAuthToken();
            const response = await axios.post(
                `${BASE_URL}/admin/category/sub-categories`,
                data,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept-Language': 'ar',
                        'lang': 'ar',
                    },
                }
            );

            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'فشل في إضافة الفئة الفرعية');
        }
    }
);

// Update subcategory
export const updateSubCategory = createAsyncThunk(
    'adminCategories/updateSubCategory',
    async ({ categoryId, subCategoryId, data }, { rejectWithValue }) => {
        try {
            const token = getAuthToken();
            const response = await axios.post(
                `${BASE_URL}/admin/category/${categoryId}/sub-categories/${subCategoryId}`,
                data,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept-Language': 'ar',
                        'lang': 'ar',
                    },
                }
            );

            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'فشل في تحديث الفئة الفرعية');
        }
    }
);

// Delete subcategory
export const deleteSubCategory = createAsyncThunk(
    'adminCategories/deleteSubCategory',
    async ({ categoryId, subCategoryId }, { rejectWithValue }) => {
        try {
            const token = getAuthToken();
            const response = await axios.delete(
                `${BASE_URL}/admin/category/${categoryId}/sub-categories/${subCategoryId}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept-Language': 'ar',
                        'lang': 'ar',
                    },
                }
            );

            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'فشل في حذف الفئة الفرعية');
        }
    }
);

const adminCategoriesSlice = createSlice({
    name: 'adminCategories',
    initialState: {
        categories: [],
        subCategories: [],
        loading: false,
        subCategoriesLoading: false,
        updating: false,
        error: null,
        success: null,
    },
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccess: (state) => {
            state.success = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch Categories
            .addCase(fetchCategories.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCategories.fulfilled, (state, action) => {
                state.loading = false;
                state.categories = action.payload.data || [];
            })
            .addCase(fetchCategories.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Update Category
            .addCase(updateCategory.pending, (state) => {
                state.updating = true;
                state.error = null;
            })
            .addCase(updateCategory.fulfilled, (state, action) => {
                state.updating = false;
                state.success = action.payload.message || 'تم تحديث الفئة بنجاح';
            })
            .addCase(updateCategory.rejected, (state, action) => {
                state.updating = false;
                state.error = action.payload;
            })

            // Fetch SubCategories
            .addCase(fetchSubCategories.pending, (state) => {
                state.subCategoriesLoading = true;
                state.error = null;
            })
            .addCase(fetchSubCategories.fulfilled, (state, action) => {
                state.subCategoriesLoading = false;
                state.subCategories = action.payload.data || [];
            })
            .addCase(fetchSubCategories.rejected, (state, action) => {
                state.subCategoriesLoading = false;
                state.error = action.payload;
            })

            // Add SubCategory
            .addCase(addSubCategory.pending, (state) => {
                state.updating = true;
                state.error = null;
            })
            .addCase(addSubCategory.fulfilled, (state, action) => {
                state.updating = false;
                state.success = action.payload.message || 'تم إضافة الفئة الفرعية بنجاح';
            })
            .addCase(addSubCategory.rejected, (state, action) => {
                state.updating = false;
                state.error = action.payload;
            })

            // Update SubCategory
            .addCase(updateSubCategory.pending, (state) => {
                state.updating = true;
                state.error = null;
            })
            .addCase(updateSubCategory.fulfilled, (state, action) => {
                state.updating = false;
                state.success = action.payload.message || 'تم تحديث الفئة الفرعية بنجاح';
            })
            .addCase(updateSubCategory.rejected, (state, action) => {
                state.updating = false;
                state.error = action.payload;
            })

            // Delete SubCategory
            .addCase(deleteSubCategory.pending, (state) => {
                state.updating = true;
                state.error = null;
            })
            .addCase(deleteSubCategory.fulfilled, (state, action) => {
                state.updating = false;
                state.success = action.payload.message || 'تم حذف الفئة الفرعية بنجاح';
            })
            .addCase(deleteSubCategory.rejected, (state, action) => {
                state.updating = false;
                state.error = action.payload;
            });
    },
});

export const { clearError, clearSuccess } = adminCategoriesSlice.actions;
export default adminCategoriesSlice.reducer;

